import { useRouter } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { type APIRedirect, RedirectReason } from '@/types'

type UseListingResponseParams = {
    redirect: () => APIRedirect | undefined
    hasError: () => boolean
    fetchStatus: () => 404 | 410 | undefined
    isEmpty: () => boolean
}

export function useListingResponse({ redirect, hasError, fetchStatus, isEmpty }: UseListingResponseParams) {
    const appStore = useAppStore()
    const router = useRouter()
    const { getCategoryRoute } = useUrl()

    // TODO deduplicate with Game.vue
    function processRedirectResponse({ redirectTarget, status: statusCode }: APIRedirect) {
        let redirectURL = ''
        if (redirectTarget.type === 'game') {
            redirectURL = router.resolve({
                name: ROUTE_NAMES.GAME_PAGE,
                params: { game: redirectTarget.game_hru },
                query: router.currentRoute.value.query,
            }).href
        } else if (redirectTarget.type === 'category') {
            redirectURL = router.resolve({
                ...getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
                query:
                    redirectTarget.reason !== RedirectReason.PAGE_OUT_OF_BOUNDS
                        ? router.currentRoute.value.query
                        : undefined,
            }).href
        } else if (redirectTarget.type === 'main-page') {
            redirectURL = router.resolve({
                name: ROUTE_NAMES.MAIN,
                query: router.currentRoute.value.query,
            }).href
        }

        appStore.setHeaders({
            'X-BFF-Redirect-Mechanism': redirectTarget.mechanism,
            'X-BFF-Redirect-Reason': redirectTarget.reason,
        })
        appStore.setPageStatus(statusCode)
        appStore.setTargetURL(redirectURL)
    }

    function processResponse() {
        const redirectValue = redirect()
        if (redirectValue) {
            processRedirectResponse(redirectValue)
            return
        }

        if (hasError()) {
            appStore.setPageStatus(500)
            return
        }

        const status = fetchStatus()
        if (status) {
            appStore.setPageStatus(status)
            return
        }

        if (isEmpty()) {
            appStore.setPageStatus(404)
        }
    }

    return { processResponse }
}
